import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardPreview,
  CardFooter,
  Button,
  Field,
  Combobox,
  Option,
  Checkbox,
  Input,
  Label,
  CounterBadge,
  Divider,
  Body1Strong,
} from "@fluentui/react-components";
import { DismissCircleRegular, SearchRegular, DatabaseRegular } from "@fluentui/react-icons";
import { InvestmentService } from "../../service/InvestmentService";
import { PortfolioService } from "../../service/PortfolioService";
import { AppContext } from "./context/AppContext";
import SubHeader from "./SubHeader";
import Loader from "./Loader";
import { writeToSheet } from "../../utils/officedocumenthelper";
import { DataAreaType } from "../../types/app.enums";
import { InvestmentsHeaders, PortfolioTypes } from "../../constants/app.header.constants";
import { getProfileFromStorage } from "../../utils/profilepreferencesave";
import { getStringFromStorage } from "../../utils/storagehelper";
import { ProfileSettings } from "../../types/app.storage.enums";
import { useInvestmentStyles, useStackClassName } from "./style/investment.style";
import { useLocation, useNavigate } from "react-router-dom";
import { isValidUser } from "../../utils/common";

const Investment = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState([PortfolioTypes[0].label]);
  const [portfoliolist, setPortfoliolist] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState([]);
  const [selectedPortfolioValue, setSelectedPortfolioValue] = useState("");
  const [selectionSetName, setSelectionSetName] = useState("");
  const [selectionSetId, setSelectionSetId] = useState("");
  const [investmentlist, setInvestmentList] = useState([]);
  const [selectallinvestment, setSelectAllInvestment] = useState<any>(false);
  const [searchInvestmentText, setSearchInvestmentText] = useState("");
  const styles = useInvestmentStyles();
  const investmentService = new InvestmentService();
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dataState = location.state;
  const populatePortfolios = (profileid) => {
    new PortfolioService()
      .getPortfolios(profileid)
      .then((result) => {
        setPortfoliolist(() => (Array.isArray(result) ? result : []));
      })
      .catch((error) => {
        console.log("Error: " + error);
        setPortfoliolist(() => []);
      })
      .finally(() => {
        setIsLoading(() => false);
      });
  };

  const portfolioTypeChange = (_, data) => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      setSelectedType(() => data.selectedOptions);
      setSelectedPortfolio(() => []);
      setSelectedPortfolioValue("");
      setInvestmentList(() => []);
      setSelectionSetId("");
      setSelectionSetName("");
    }
  };

  const portfolioChange = (_, data) => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      setIsLoading(() => true);
      setSelectAllInvestment(() => false);
      setInvestmentList(() => []);
      setSelectedPortfolio(() => data.selectedOptions);
      setSelectedPortfolioValue(data.optionText);
      investmentService
        .getInvestments(
          DataAreaType[selectedType[0]],
          context.selectedProfile.profileID,
          data.selectedOptions[0],
          selectionSetId
        )
        .then((result) => {
          if (result && result.length > 0) {
            setInvestmentList(() => result.map((obj) => ({ ...obj, selected: false })));
          } else {
            setInvestmentList(() => []);
          }
        })
        .catch((error) => {
          console.log("Error: " + error);
          setInvestmentList(() => []);
        })
        .finally(() => {
          setIsLoading(() => false);
        });
    }
  };
  const handleSelectionSetSearchChanges = () => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      const stateData = {
        portfolioType: selectedType,
        portfolio: selectedPortfolio,
        portfolioValue: selectedPortfolioValue,
        selectionSetId: selectionSetId,
        selectionSetName: selectionSetName,
      };
      navigate("/SelectionSet", { state: stateData });
    }
  };
  const handleInvestmentSearchChange = (e) => {
    setSearchInvestmentText(() => e.target.value);
  };

  const handleInvestmentSearchClear = () => {
    setSearchInvestmentText(() => "");
  };

  const investmentSelectionHandler = (e, id) => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      const investmentList = [...investmentlist];
      const objIndex = investmentList.findIndex((obj) => obj.investmentId == id);
      investmentList[objIndex].selected = !investmentList[objIndex].selected;
      setInvestmentList(() => investmentList);
      selectAllInvestmentChange();
      e.preventDefault();
    }
  };

  const selectAllInvestmentChange = () => {
    const selectedInvestment = investmentlist.filter((item) => {
      return item.selected;
    });
    if (selectedInvestment.length === 0) setSelectAllInvestment(false);
    else if (selectedInvestment.length === investmentlist.length) setSelectAllInvestment(true);
    else setSelectAllInvestment("mixed");
  };

  const selectAllHandler = (_evt, data) => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      setSelectAllInvestment(() => data.checked);
      setInvestmentList((pre) => pre.map((obj) => ({ ...obj, selected: data.checked })));
    }
  };

  const getInvestmentClickHandler = () => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      const selectedInvestment = investmentlist.filter((item) => {
        return (
          ((searchInvestmentText.length >= 3 &&
            item.investmentName.toLowerCase().includes(searchInvestmentText.toLowerCase())) ||
            searchInvestmentText.length < 3) &&
          item.selected
        );
      });
      if (selectedInvestment && selectedInvestment.length > 0) {
        const newData = selectedInvestment.map((item) => [item.investmentId, item.investmentName]);
        writeToSheet(InvestmentsHeaders, newData).catch((error) => {
          console.log("Error: " + error);
        });
      }
    }
  };
  const clearSelectionSet = () => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      setSelectionSetId("");
      setSelectionSetName("");
      if (selectedPortfolio !== null && selectedPortfolio.length > 0) {
        setIsLoading(() => true);
        investmentService
          .getInvestments(DataAreaType[selectedType[0]], context.selectedProfile.profileID, selectedPortfolio[0], "")
          .then((result) => {
            if (result && result.length > 0) {
              setInvestmentList(() => result.map((obj) => ({ ...obj, selected: false })));
            } else {
              setInvestmentList(() => []);
            }
          })
          .catch((error) => {
            console.log("Error: " + error);
            setInvestmentList(() => []);
          })
          .finally(() => {
            setIsLoading(() => false);
          });
      }
    }
  };
  const resetData = () => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      setSelectAllInvestment(() => false);
      setInvestmentList(() => []);
      setSelectedType([dataState.portfolioType[0]]);
      setSelectionSetName(dataState.selectionSetName);
      setSelectionSetId(dataState.selectionSetId);
      if (dataState.portfolio !== null && dataState.portfolio.length > 0) {
        setSelectedPortfolio([dataState.portfolio[0]]);
        setSelectedPortfolioValue(dataState.portfolioValue);
        setIsLoading(() => true);
        investmentService
          .getInvestments(
            DataAreaType[dataState.portfolioType[0]],
            context.selectedProfile.profileID,
            dataState.portfolio[0],
            dataState.selectionSetId
          )
          .then((result) => {
            if (result && result.length > 0) {
              setInvestmentList(() => result.map((obj) => ({ ...obj, selected: false })));
            } else {
              setInvestmentList(() => []);
            }
          })
          .catch((error) => {
            console.log("Error: " + error);
            setInvestmentList(() => []);
          })
          .finally(() => {
            setIsLoading(() => false);
          });
      }
    }
  };

  useEffect(() => {
    if (isValidUser()) {
      navigate("/home");
      return;
    } else {
      if (getStringFromStorage(ProfileSettings.ProfileId)) {
        getProfileFromStorage().then((profile) => {
          context.setSelectedProfile(profile);
          populatePortfolios(profile.profileID);
        });
      } else {
        populatePortfolios(context.selectedProfile.profileID);
      }
      if (dataState !== null) {
        resetData();
      }
    }
  }, []);

  return (
    <>
      <SubHeader title="Investments" subtitle="Data" icon={<DatabaseRegular></DatabaseRegular>}></SubHeader>
      <div className={styles.root}>
        <Card className={styles.card} appearance="subtle">
          <CardPreview className={styles.cardPreview}>
            <div className={useStackClassName()}>
              <Field label="Portfolio Type" className={styles.input}>
                <Combobox
                  id="cmbboxPortfolioType"
                  selectedOptions={selectedType}
                  onOptionSelect={portfolioTypeChange}
                  listbox={{ className: styles.listbox }}
                  defaultValue={PortfolioTypes[0].label}
                  defaultSelectedOptions={[PortfolioTypes[0].label]}
                  value={selectedType[0]}
                >
                  {PortfolioTypes.map((item) => {
                    return (
                      <Option key={item.name} value={item.label}>
                        {item.label}
                      </Option>
                    );
                  })}
                </Combobox>
              </Field>
              <Field label="Portfolio" className={styles.input}>
                <Combobox
                  selectedOptions={selectedPortfolio}
                  placeholder="--Select Portfolio--"
                  onOptionSelect={portfolioChange}
                  listbox={{ className: styles.listbox }}
                >
                  {portfoliolist
                    .filter((obj) => {
                      return obj.portfolioType === selectedType[0];
                    })
                    .map((item) => {
                      return (
                        <Option key={item.portfolioID} value={item.portfolioID}>
                          {item.portfolioName}
                        </Option>
                      );
                    })}
                  <Option key="-1" value="-1">
                    (All Portfolios)
                  </Option>
                </Combobox>
              </Field>
              <Field label="Selection Set" className={styles.input}>
                <Input
                  type="text"
                  placeholder="Search selection set"
                  value={selectionSetName}
                  contentAfter={
                    <>
                      <DismissCircleRegular className={styles.cursorPointer} onClick={clearSelectionSet} />{" "}
                      <SearchRegular
                        className={styles.cursorPointer}
                        onClick={handleSelectionSetSearchChanges}
                      ></SearchRegular>
                    </>
                  }
                />
              </Field>
              <Field className={styles.divider}>
                <Divider />
              </Field>

              <Field className={styles.input}>
                <Body1Strong className={styles.bodyPaddingBottom}>
                  Investments{"  "}
                  <CounterBadge
                    count={
                      investmentlist.filter(
                        (investment) =>
                          (searchInvestmentText.length >= 3 &&
                            investment.investmentName.toLowerCase().includes(searchInvestmentText.toLowerCase())) ||
                          searchInvestmentText.length < 3
                      ).length
                    }
                    appearance="filled"
                  ></CounterBadge>
                </Body1Strong>
              </Field>
              <Field className={styles.input}>
                <Input
                  disabled={!investmentlist || investmentlist.length === 0}
                  placeholder="Search Investments"
                  contentBefore={<SearchRegular></SearchRegular>}
                  contentAfter={
                    <DismissCircleRegular className={styles.cursorPointer} onClick={handleInvestmentSearchClear} />
                  }
                  value={searchInvestmentText}
                  onChange={handleInvestmentSearchChange}
                />
              </Field>
              <Field className={styles.input}>
                <Checkbox
                  disabled={!investmentlist || investmentlist.length === 0}
                  checked={selectallinvestment}
                  label="Select All"
                  onChange={selectAllHandler}
                />
              </Field>
              <Field className={styles.input}>
                <div className={styles.checkboxContainer}>
                  {investmentlist
                    ?.filter((investment) => {
                      const searchText = searchInvestmentText.toLowerCase();
                      return (
                        searchInvestmentText.length < 3 || investment.investmentName.toLowerCase().includes(searchText)
                      );
                    })
                    .map((item) => (
                      <div key={item.investmentId} onClick={(e) => investmentSelectionHandler(e, item.investmentId)}>
                        <Checkbox value={item.investmentId} checked={item.selected} label={item.investmentName} />
                      </div>
                    ))}
                </div>
              </Field>
            </div>
          </CardPreview>
          {investmentlist.length === 0 && !isLoading && selectedPortfolio.length > 0 && (
            <div className={styles.centeralignfooter}>
              <Label className={styles.warningLabel} weight="semibold">
                No Investment(s) Found!
              </Label>
            </div>
          )}
          <CardFooter dir="rtl">
            <Button
              key="getInvest"
              appearance="primary"
              size="small"
              onClick={getInvestmentClickHandler}
              disabled={investmentlist.findIndex((obj) => obj.selected) == -1}
              className={styles.button}
            >
              Download
            </Button>
          </CardFooter>
        </Card>
        {isLoading && <Loader></Loader>}
      </div>
    </>
  );
};
export default Investment;
